// src/components/GroupSelection.js
import { useState } from "react";
import {
  Button, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle,
  FormControl, FormLabel, Radio, RadioGroup,
  FormControlLabel, TextField, Typography,
} from "@mui/material";
import { useJoinGroupByJoinCode } from "../hooks/useGroups";
import { useSubmitRequest } from "../hooks/useGroupRequests";
import * as groupsService from "../services/groupsService";
import * as usersService from "../services/usersService";

export default function GroupSelection({ userId, onComplete }) {
  const joinGroupMutation = useJoinGroupByJoinCode();
  const submitRequestMutation = useSubmitRequest();
  const [joinCode, setJoinCode] = useState("");
  const [resolvedGroup, setResolvedGroup] = useState(null);
  const [checkingCode, setCheckingCode] = useState(false);
  const [codeError, setCodeError] = useState(null);
  const [requestMode, setRequestMode] = useState("join");
  const [newGroupName, setNewGroupName] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isParentChoice, setIsParentChoice] = useState("no");
  const [error, setError] = useState(null);

  const isCreateMode = requestMode === "create";

  const switchMode = (mode) => {
    setRequestMode(mode);
    setCodeError(null);
  };

  const handleContinue = async () => {
    if (isCreateMode) {
      if (!newGroupName.trim()) return;
      setConfirming(true);
      return;
    }

    const code = joinCode.trim();
    if (!code) return;

    setCheckingCode(true);
    setCodeError(null);
    try {
      const group = await groupsService.findGroupByJoinCode({ code });
      if (!group) {
        setCodeError("Invalid or expired join code.");
        return;
      }
      setResolvedGroup(group);
      setConfirming(true);
    } catch {
      setCodeError("Something went wrong checking that code. Please try again.");
    } finally {
      setCheckingCode(false);
    }
  };

  const handleConfirm = async () => {
    if (isCreateMode && !newGroupName.trim()) return;
    if (!isCreateMode && !resolvedGroup) return;
    setSaving(true);
    setError(null);
    const isParent = isParentChoice === "yes";
    try {
      if (isCreateMode) {
        await submitRequestMutation.mutateAsync({ fromUserId: userId, requestType: "create", newGroupName: newGroupName.trim() });
        await usersService.setIsParent({ userId, isParent });
        onComplete({ isParent });
      } else {
        const joinedGroup = await joinGroupMutation.mutateAsync({ code: joinCode.trim() });
        await usersService.setIsParent({ userId, isParent });
        onComplete({ isParent, joinedGroupId: joinedGroup?.id ?? resolvedGroup.id });
      }
    } catch {
      setError("Failed to submit request. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      {!confirming ? (
        <DialogContent sx={{ textAlign: "center", pt: 4 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Welcome!</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {isCreateMode
              ? "Request a brand-new group. Your request will be sent to an admin for approval."
              : "Enter the join code your group's Leader shared with you."}
          </Typography>
          {isCreateMode ? (
            <TextField
              fullWidth
              size="small"
              label="New group name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              sx={{ mb: 1 }}
            />
          ) : (
            <TextField
              fullWidth
              size="small"
              label="Join code"
              value={joinCode}
              onChange={(e) => { setJoinCode(e.target.value.toUpperCase()); setCodeError(null); }}
              inputProps={{ maxLength: 6, style: { textAlign: "center", letterSpacing: "0.3em" } }}
              sx={{ mb: 1 }}
            />
          )}
          {!isCreateMode && codeError && (
            <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
              {codeError}
            </Typography>
          )}
          <Typography
            variant="body2"
            color="primary"
            onClick={() => switchMode(isCreateMode ? "join" : "create")}
            sx={{ mb: 3, cursor: "pointer", textDecoration: "underline", display: "inline-block" }}
          >
            {isCreateMode ? "Back to group selection" : "Don't have a join code? Request a new group"}
          </Typography>
          <Button
            variant="contained"
            fullWidth
            disabled={isCreateMode ? !newGroupName.trim() : (!joinCode.trim() || checkingCode)}
            onClick={handleContinue}
          >
            {isCreateMode ? "Request New Group" : (checkingCode ? "Checking..." : "Continue")}
          </Button>
        </DialogContent>
      ) : (
        <>
          <DialogTitle>{isCreateMode ? "Request a New Group" : "Join Group"}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {isCreateMode ? (
                <>Send a request to create <strong>{newGroupName.trim()}</strong>? An
                admin will review and approve it. You can use the app while you wait
                — your lists will appear automatically once approved.</>
              ) : (
                <>Join <strong>{resolvedGroup?.name}</strong>? You'll be added right
                away — no approval needed.</>
              )}
            </DialogContentText>
            <FormControl sx={{ mt: 3 }}>
              <FormLabel>
                Are you a parent/guardian/caregiver/etc who will be managing a list for someone who cannot manage their own list?
              </FormLabel>
              <RadioGroup
                row
                value={isParentChoice}
                onChange={(e) => setIsParentChoice(e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
              <Typography variant="caption" color="text.secondary">
                You can change this later in Settings.
              </Typography>
              {error && (
                <Typography variant="caption" color="error">
                  {error}
                </Typography>
              )}
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirming(false)}>Go Back</Button>
            <Button
              variant="contained"
              color="success"
              disabled={saving}
              onClick={handleConfirm}
            >
              {saving ? "Sending..." : isCreateMode ? "Send Request" : "Join Group"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
